import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { NorlysColor, NorlysFont } from '~src/branding';
import { Heading } from '~src/components/Typography';
import { monthNames } from '~src/constants/date';

import { Legend, LegendNorlys } from '../Legend';
import { EnergyCostGraphTooltip } from '../Tooltip/EnergyCostTooltip';
import { DEFAULT_TITLE } from './constants';
import { GraphWrapperNorlys, TitleNorlys } from './style';
import { DesktopEnergyCostGraphProps, EnergyCostGraphProps } from './types';

const DesktopEnergyCostGraphCommon = ({
  values,
  beforeColor,
  afterColor,
  barSize = 10,
  referenceLines = false,
  tooltipProps,
}: DesktopEnergyCostGraphProps) => {
  const data = values.map(([before, after], idx) => ({
    month: monthNames[idx].slice(0, 3),
    annualPriceBefore: before,
    annualPriceAfter: after,
  }));

  const axisProps = { axisLine: false, tickLine: false };

  const barProps = { barSize, radius: [10, 10, 0, 0] as [number, number, number, number] };

  return (
    <ResponsiveContainer width='100%' height={400}>
      <BarChart {...{ data, barGap: -barSize }}>
        <Tooltip
          cursor={false}
          content={<EnergyCostGraphTooltip {...{ beforeColor, afterColor, ...tooltipProps }} />}
        />
        {referenceLines && <CartesianGrid {...{ vertical: false }} />}
        <XAxis {...{ dataKey: 'month', ...axisProps }} />
        <YAxis {...axisProps} />
        <Bar
          {...{
            dataKey: 'annualPriceBefore',
            fill: beforeColor,
            ...barProps,
          }}
        />
        <Bar
          {...{
            dataKey: 'annualPriceAfter',
            fill: afterColor,
            ...barProps,
          }}
        />
      </BarChart>
    </ResponsiveContainer>
  );
};

export const DesktopEnergyCostGraph = ({
  values,
  legendEntries,
  title = DEFAULT_TITLE,
  variant,
  ...rest
}: EnergyCostGraphProps) => {
  const isWide = variant === 'wide';
  const barSize = isWide ? 50 : 30;

  return (
    <>
      {typeof title === 'string' ? (
        <Heading level={2} as='h2'>
          {title}
        </Heading>
      ) : (
        title
      )}
      <Legend entries={legendEntries} />
      <DesktopEnergyCostGraphCommon {...{ values, barSize, ...rest }} />
    </>
  );
};

export const DesktopEnergyCostGraphNorlys = ({
  values,
  legendEntries,
  title = DEFAULT_TITLE,
  variant,
}: EnergyCostGraphProps) => {
  return (
    <div {...{ style: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' } }}>
      {typeof title === 'string' ? <TitleNorlys>{title}</TitleNorlys> : title}
      <GraphWrapperNorlys>
        <DesktopEnergyCostGraphCommon
          {...{
            beforeColor: NorlysColor.secondary.lightPurple,
            afterColor: NorlysColor.secondary.purple,
            values,
            barSize: variant === 'wide' ? 25 : undefined,
            tooltipProps: { beforeTextColor: 'black', fontStyle: NorlysFont.style('heading', 14) },
          }}
        />
        <LegendNorlys
          textStyle={NorlysFont.style('default', 14)}
          numberStyle={NorlysFont.style('heading', 20)}
          entries={legendEntries}
          direction='column'
          style={{ marginLeft: '8px', width: '200px' }}
        />
      </GraphWrapperNorlys>
    </div>
  );
};
